import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // 'customer' | 'restaurant' | null
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("cravve-token");
    if (!token) {
      setUser(null);
      setRole(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setRole(data.role);
    } catch {
      localStorage.removeItem("cravve-token");
      localStorage.removeItem("cravve-role");
      setUser(null);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setSession = ({ token, role: r, user: u }) => {
    localStorage.setItem("cravve-token", token);
    localStorage.setItem("cravve-role", r);
    setUser(u);
    setRole(r);
  };

  const signinCustomer = async (phone, password) => {
    const { data } = await api.post("/auth/customer/signin", { phone, password });
    setSession(data);
    return data;
  };

  const signupCustomer = async (payload) => {
    const { data } = await api.post("/auth/customer/signup", payload);
    setSession(data);
    return data;
  };

  const signinRestaurant = async (phone, password) => {
    const { data } = await api.post("/auth/restaurant/signin", { phone, password });
    setSession(data);
    return data;
  };

  const signupRestaurant = async (payload) => {
    const { data } = await api.post("/auth/restaurant/signup", payload);
    setSession(data);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("cravve-token");
    localStorage.removeItem("cravve-role");
    setUser(null);
    setRole(null);
  };

  const value = {
    user,
    role,
    loading,
    isAuthenticated: !!user,
    isCustomer: role === "customer",
    isRestaurant: role === "restaurant",
    signinCustomer,
    signupCustomer,
    signinRestaurant,
    signupRestaurant,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
